
/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Update User Profile
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       4th April 2025

*******************************************************************************************/

import React                            from 'react'
import { Box,Typography }               from '@mui/material';
import { 
    useUser,
    useUserProfile
}                                       from 'contexts';
import { 
    DraggableDialog
}                                       from 'components';
import {
    UserProfileForm
}                                       from 'components/forms'

const TIMEOUT_INTERVAL = 2000;

export const UpdateUserProfile      = (props) => {   

    const {isAuthenticated}         = useUser();
    const {
        loading,
        queried,
        data,
        update,
    }                               = useUserProfile();
    const [open, setOpen]           = React.useState(false);

    const handleOpen                = React.useCallback(() => setOpen(true), []);
    const handleClose               = React.useCallback(() => setOpen(false), []);

    const handleSubmitProfile = React.useCallback(({_id, ...formData}) => new Promise(resolve => (
        update({_id, ...formData})
            .then(() => {
                handleClose();
                resolve({});
            })
            .catch(({errors}) => resolve(errors))
    )),[update,handleClose])

    const timeout = React.useRef(null);
    React.useEffect(() => {
        if(!isAuthenticated || !queried || !data?._id)
            return;

        // Is Updated
        const updated = Boolean(data?.updated);

        // If not updated, open modal on timeout
        clearTimeout(timeout.current);
        if(!updated){
            timeout.current = setTimeout(() => {
                handleOpen();
            },TIMEOUT_INTERVAL)
        }else{
            handleClose();
        }
        return () => {
            clearTimeout(timeout.current);
        }
    }, [data?.updated, data?._id, queried, isAuthenticated, handleOpen, handleClose]);

    return (
        <DraggableDialog 
            title       = "Update Your Profile"
            maxWidth    = {"sm"}
            showButtons = {false}
            {...props}
            open        = {open} 
            onClose     = {null} 
            onCancel    = {handleClose}
        >
            <Typography align="justify" paragraph>
                Please take a moment to update your profile and share a bit more about yourself.
            </Typography>
            <Box>
                <UserProfileForm 
                    disabled            = {loading}
                    formData            = {data}
                    onSubmit            = {handleSubmitProfile}
                    showCancelButton    = {false}
                    sticky              = {true}
                />
            </Box>
        </DraggableDialog>
    )
}

export default UpdateUserProfile;
